import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { iconForNAICS } from "../../../helper/sector";
import { SIDEBAR } from "./sidebarVisual";
import { MutedBlock } from "./SidebarPrimitives";

/**
 * Normalize classification levels whether API sends an array or keyed object.
 */
export function normalizeClassificationLevels(levels) {
  if (!levels) return [];
  if (Array.isArray(levels)) {
    return levels.filter((level) => level && (level.description || level.code || level.type));
  }
  if (typeof levels === "object") {
    return Object.keys(levels)
      .map(Number)
      .filter((key) => Number.isFinite(key))
      .sort((a, b) => a - b)
      .map((key) => levels[key])
      .filter((level) => level && (level.description || level.code || level.type));
  }
  return [];
}

/**
 * Compact V2 classification card — muted gray surface matching Details blocks.
 */
export default function ClassificationCardV2({ levels }) {
  const normalized = useMemo(() => normalizeClassificationLevels(levels), [levels]);
  const [index, setIndex] = useState(() => Math.max(0, normalized.length - 1));

  if (!normalized.length) return null;

  const safeIndex = Math.min(index, normalized.length - 1);
  const current = normalized[safeIndex];
  const canGoDeeper = safeIndex < normalized.length - 1;
  const canGoShallower = safeIndex > 0;

  return (
    <MutedBlock testId="classification-card-v2">
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: SIDEBAR.gap.withinCard }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: "rgba(88, 89, 91, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            flexShrink: 0,
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        >
          {iconForNAICS(current.code)}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.35 }}>
            {current.type ? (
              <Typography sx={{ ...SIDEBAR.type.label, mb: 0 }}>{current.type}</Typography>
            ) : null}
            {current.code ? (
              <Typography sx={{ ...SIDEBAR.type.meta, color: "text.secondary" }}>
                {current.code}
              </Typography>
            ) : null}
            {current.level != null ? (
              <Typography sx={{ ...SIDEBAR.type.meta }}>L{current.level}</Typography>
            ) : null}
          </Box>
          <Typography
            sx={{
              ...SIDEBAR.type.body,
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            {current.description || "Unnamed classification"}
          </Typography>
          {typeof current.likelihood === "number" ? (
            <Typography sx={{ ...SIDEBAR.type.meta, mt: 0.4 }}>
              Confidence {Math.round(current.likelihood * 100)}%
            </Typography>
          ) : null}
        </Box>

        {normalized.length > 1 ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.15, flexShrink: 0 }}>
            <IconButton
              size="small"
              aria-label="Shallower classification level"
              disabled={!canGoShallower}
              onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
              sx={{ color: "text.secondary" }}
            >
              <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Deeper classification level"
              disabled={!canGoDeeper}
              onClick={() => setIndex((prev) => Math.min(normalized.length - 1, prev + 1))}
              sx={{ color: "text.secondary" }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </MutedBlock>
  );
}

ClassificationCardV2.propTypes = {
  levels: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};
